import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Upload, QrCode, Lock, Check } from 'lucide-react';
import { SEO } from '../../components/common/SEO';
import { Header } from '../../components/layout/Header';
import { ProductFields } from '../../components/smartqr/ProductFields';
import { LocationFields } from '../../components/smartqr/LocationFields';
import { ServicesPromo } from '../../components/smartqr/ServicesPromo';
import { TermsAgreement, SMART_QR_TERMS } from '../../components/smartqr/SmartQRTerms';
import { API, uploadImage, imgSrc, inputCls, labelCls, PREMIUM_AMOUNT, SMART_QR_CATEGORIES, TERMS_VERSION } from '../../components/smartqr/smartqrApi';

const emptyProduct = { name: '', price: '', image: '', description: '' };
const optionalLinks = [
  ['google_review_link', 'Google Review Link', 'https://g.page/r/...'], ['website', 'Website', 'https://'],
  ['instagram', 'Instagram', 'https://instagram.com/...'], ['facebook', 'Facebook', 'https://facebook.com/...'], ['youtube', 'YouTube', 'https://youtube.com/...'],
];
const TERMS_LABEL = 'I have read and agree to the Terms and Conditions and understand that I am responsible for the accuracy and legality of the business information, products, images and links I submit.';

export const SmartQRCreatePage = ({ adminMode = false }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: '', category: '', whatsapp: '', phone: '', logo: '', google_review_link: '', website: '', instagram: '', facebook: '', youtube: '' });
  const [loc, setLoc] = useState({ country: 'India', state: '', district: '', city: '', area: '', pincode: '', address: '' });
  const [products, setProducts] = useState([]);
  const [terms, setTerms] = useState(adminMode);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try { set('logo', await uploadImage(file)); } catch { toast.error('Logo upload failed'); }
    setUploadingLogo(false);
  };

  const submit = async (skipProducts = false) => {
    if (!form.business_name.trim() || !form.category || !loc.country.trim() || !loc.state.trim() || !loc.city.trim() || !loc.area.trim() || !form.whatsapp.trim()) {
      toast.error('Please fill Business Name, Category, Country, State, City/Town, Area and WhatsApp number');
      return;
    }
    if (!terms) { toast.error('Please read and accept the Terms and Conditions to continue'); return; }
    const validProducts = skipProducts ? [] : products.filter((p) => p.name.trim());
    setLoading(true);
    try {
      const payload = { ...form, ...loc, terms_accepted: true, terms_version: TERMS_VERSION,
        products: validProducts.map((p) => ({ ...p, price: p.price || null, image: p.image || null, description: p.description || null })) };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      if (adminMode) {
        const res = await axios.post(`${API}/api/smart-qr/admin/businesses`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        toast.success('Business created');
        navigate(`/admin/smart-qr/${res.data.business_id}`);
        return;
      }
      const res = await axios.post(`${API}/api/smart-qr/businesses`, payload);
      localStorage.setItem('smartqr_my_url', res.data.my_qr_url);
      toast.success('Your Smart Business QR is ready!');
      navigate(`/smart-business-qr/my/${res.data.business_id}/${res.data.owner_key}?new=1`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) && d[0]?.msg ? d[0].msg.replace('Value error, ', '') : 'Could not create your business page. Please check the details.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="smart-qr-create-page">
      <SEO title="Create Your Free Smart Business QR - JustOffs" description="Create your free JustOffs business page and permanent QR in 2 minutes." />
      <Header />
      <div className="max-w-xl mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-extrabold text-slate-800">{adminMode ? 'Admin: Add Smart QR Business' : 'Create My Free Business QR'}</h1>
          <p className="text-sm text-slate-500">{adminMode ? 'Creates a real Smart QR business record (owner link available in admin).' : 'Takes about 2 minutes. No payment required.'}</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 mb-4">
          <h2 className="text-base font-bold text-slate-800">Business Details</h2>
          <div><label className={labelCls}>Business Name *</label><input value={form.business_name} onChange={(e) => set('business_name', e.target.value)} className={inputCls} placeholder="e.g. Sharma Sweets" maxLength={80} data-testid="biz-name-input" /></div>
          <div><label className={labelCls}>Category *</label>
            <select value={form.category} onChange={(e) => set('category', e.target.value)} className={inputCls} data-testid="biz-category-select">
              <option value="">Select category</option>
              {SMART_QR_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>WhatsApp Number *</label><input value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className={inputCls} placeholder="10-digit mobile" inputMode="tel" maxLength={15} data-testid="biz-whatsapp-input" /></div>
            <div><label className={labelCls}>Phone (for calls)</label><input value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputCls} placeholder="Same as WhatsApp" inputMode="tel" maxLength={15} data-testid="biz-phone-input" /></div>
          </div>
          <div><label className={labelCls}>Business Logo / Shop Picture (optional)</label>
            <label className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 text-sm text-slate-500 cursor-pointer hover:border-orange-400">
              {form.logo ? <img src={imgSrc(form.logo)} alt="logo" className="w-10 h-10 rounded-lg object-cover" /> : <Upload className="w-5 h-5" />}
              <span>{uploadingLogo ? 'Uploading...' : form.logo ? 'Change picture' : 'Upload logo or shop picture'}</span>
              <input type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="biz-logo-input" />
            </label>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 mb-4" data-testid="location-section">
          <h2 className="text-base font-bold text-slate-800">Location</h2>
          <LocationFields value={loc} onChange={setLoc} testPrefix="biz" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3 mb-4" data-testid="links-section">
          <h2 className="text-base font-bold text-slate-800">Google Review, Website & Social links <span className="text-slate-400 font-medium text-xs">(optional)</span></h2>
          {optionalLinks.map(([k, label, ph]) => (
            <div key={k}><label className={labelCls}>{label}</label><input value={form[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} placeholder={ph} data-testid={`biz-${k}-input`} /></div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 mb-4" data-testid="products-section">
          <div>
            <h2 className="text-base font-bold text-slate-800">ADD YOUR PRODUCTS / SERVICES</h2>
            <p className="text-xs text-slate-500">Optional. Add up to 3 for free — they appear on your business page. Images are optional; only your own uploaded images are shown.</p>
          </div>
          {products.map((p, i) => (
            <ProductFields key={i} index={i} value={p} onChange={(v) => setProducts(products.map((x, j) => (j === i ? v : x)))} onRemove={() => setProducts(products.filter((_, j) => j !== i))} />
          ))}
          {products.length < 3 ? (
            <button type="button" onClick={() => setProducts([...products, { ...emptyProduct }])} className="w-full border-2 border-dashed border-slate-300 text-slate-600 py-3 rounded-xl font-semibold text-sm hover:border-orange-400 hover:text-orange-600 transition flex items-center justify-center gap-2" data-testid="add-product-btn">
              <Plus className="w-4 h-4" /> ADD PRODUCT ({products.length}/3 free)
            </button>
          ) : (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center" data-testid="premium-teaser">
              <Lock className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <p className="text-sm font-semibold text-slate-800">Want to add more products and update your business anytime?</p>
              <p className="text-xs text-slate-600">Unlock Smart QR Premium for ₹{PREMIUM_AMOUNT} one time — available right after you create your free QR.</p>
            </div>
          )}
        </div>

        {!adminMode && <div className="mb-4">
          <TermsAgreement terms={SMART_QR_TERMS} title="Terms and Conditions (tap to read)" checked={terms} onChange={setTerms} label={TERMS_LABEL} testPrefix="terms" />
        </div>}

        <button onClick={() => submit(false)} disabled={loading || !terms} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-xl font-bold text-base transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="create-qr-submit-btn">
          <QrCode className="w-5 h-5" /> {loading ? 'Creating...' : 'CREATE MY FREE QR'}
        </button>
        {!terms && <p className="text-[11px] text-slate-400 text-center mt-1.5" data-testid="terms-hint">Accept the Terms and Conditions above to enable this button.</p>}
        <button onClick={() => submit(true)} disabled={loading || !terms} className="w-full mt-2 text-slate-500 py-2 text-sm font-medium hover:text-slate-700 disabled:opacity-50" data-testid="skip-products-btn">SKIP {products.length > 0 ? 'PRODUCTS ' : ''}FOR NOW — just create my QR</button>

        {!adminMode && (
          <div className="mt-6 bg-white rounded-2xl border-2 border-orange-300 p-5 sm:p-6 shadow-sm" data-testid="create-premium-section">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="bg-orange-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wide">PREMIUM</span>
              <span className="text-xs font-semibold text-slate-500">No subscription · No expiry</span>
            </div>
            <p className="text-2xl font-extrabold text-slate-800">₹{PREMIUM_AMOUNT} <span className="text-sm font-semibold text-slate-500">one-time</span></p>
            <p className="text-sm text-slate-600 mt-1">Your QR and page are free forever. Premium unlocks ongoing self-management:</p>
            <ul className="mt-3 grid sm:grid-cols-2 gap-1.5">
              {['Edit business details anytime', 'Add / edit / delete products (up to 50)', 'Order / Enquiry WhatsApp button on products', 'Logo, website & social links', 'Branded printable QR poster', 'Secure management link'].map((i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />{i}</li>)}
            </ul>
            <button type="button" onClick={() => document.querySelector('[data-testid="create-qr-submit-btn"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })} className="mt-4 w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition" data-testid="create-unlock-premium-btn"><Lock className="w-4 h-4" /> UNLOCK PREMIUM — ₹{PREMIUM_AMOUNT}</button>
            <p className="text-[11px] text-slate-500 text-center mt-2">Create your free QR first — a secure Razorpay payment link for your business is generated on your My QR page. Premium activates only after Razorpay confirms the payment.</p>
          </div>
        )}
        <div className="mt-4"><ServicesPromo /></div>
      </div>
    </div>
  );
};
